import { useRef, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Dices } from 'lucide-react';
import { COLLECTIONS, type Collection } from '@/lib/taxonomy';
import { MorseDots } from '@/components/MorseDots';
import { CollectionIcon, CollectionTitle } from '@/components/CollectionInfo';
import { useRevealProgress } from '@/hooks/useScrollLinked';
import { cn } from '@/lib/utils';

/**
 * The archive's six main holdings are compact buttons that filter the records
 * below. The two specialist ones (Pager Intercepts, Bitcoin Inscriptions) are
 * "special collections": they open their own pages, so they are links, not
 * filters, and are shown apart from the six. (`niche` in taxonomy.ts marks them.)
 */
const MAIN = COLLECTIONS.filter((c) => c.kind === 'main' && !c.niche);
const SPECIAL = COLLECTIONS.filter((c) => c.kind === 'main' && c.niche);

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

interface CollectionDeckProps {
  /** Records in the whole archive. */
  total?: number;
  /** Exact record count per collection id. */
  counts?: Record<string, number>;
  /** True until the first results arrive. */
  loading: boolean;
  /** The selected collection id, if one is chosen. */
  active: string | null;
  /** True when nothing at all is narrowing the archive (no category, format, filter or search). */
  isDefault: boolean;
  /** Choose a section (the page then carries the reader to the records). */
  onSelect: (id: string) => void;
  /** Clear EVERYTHING back to the default view (the page then carries the reader to the records). */
  onExploreAll: () => void;
  /** Open a record chosen at random from what is currently showing. */
  onRandom?: () => void;
}

/**
 * Where to start, kept as small as it can be while staying readable:
 *
 *   - One row: "All records" (the reset, and the record total) beside
 *     "Random record" (a way to simply wander in).
 *   - The six main sections as compact buttons, two across on a phone. Tapping
 *     one shows its records; what it holds is explained above those records.
 *   - Special collections: two larger cards that open their own pages.
 *
 * Search, filters and the record type are not here; they live in the toolbar
 * directly beneath, which sticks under the header.
 */
export function CollectionDeck({
  total,
  counts,
  loading,
  active,
  isDefault,
  onSelect,
  onExploreAll,
  onRandom,
}: CollectionDeckProps) {
  // 0 -> 1 as this block rises into the screen; drives the arrival animation.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useRevealProgress(rootRef, { from: 1, to: 0.5 });

  const stagger = (i: number) => ({ '--i': i }) as CSSProperties;

  return (
    <div ref={rootRef} id="archive-categories" className="px-4 pb-3 pt-4 sm:pt-5">
      <div className="mx-auto max-w-6xl">
        <div className="deck-card flex gap-2" style={stagger(0)}>
          <AllRecords isDefault={isDefault} loading={loading} total={total} onClick={onExploreAll} />
          {onRandom && (
            <button
              type="button"
              onClick={onRandom}
              disabled={loading}
              aria-label="Random record"
              className={cn(
                'inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-ink/50 bg-ivory px-4 text-ink',
                'font-engraved text-[11px] font-bold uppercase tracking-[0.14em] transition-colors',
                'hover:bg-ink/[0.08] active:bg-ink/[0.14] disabled:pointer-events-none disabled:opacity-40',
                '[-webkit-tap-highlight-color:transparent]',
                FOCUS,
              )}
            >
              <Dices aria-hidden="true" className="h-[18px] w-[18px]" />
              <span aria-hidden="true">
                Random<span className="hidden sm:inline"> record</span>
              </span>
            </button>
          )}
        </div>

        <ol aria-label="Sections of the archive" className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-3">
          {MAIN.map((c, i) => (
            <li key={c.id} className="deck-card flex" style={stagger(i + 1)}>
              <SectionButton
                collection={c}
                count={counts?.[c.id]}
                loading={loading}
                selected={active === c.id}
                onSelect={onSelect}
              />
            </li>
          ))}
        </ol>

        {SPECIAL.length > 0 && (
          <div className="deck-card mt-4" style={stagger(MAIN.length + 1)}>
            <p className="mb-2 px-1 font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-oxblood">
              Featured collections
            </p>
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {SPECIAL.map((c) => (
                <li key={c.id} className="flex min-w-0">
                  <SpecialLink collection={c} count={counts?.[c.id]} loading={loading} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * All records: the one solid dark control, so it stays the focal point. It is the
 * reset back to the whole archive, and carries the record total.
 */
function AllRecords({
  isDefault,
  loading,
  total,
  onClick,
}: {
  isDefault: boolean;
  loading: boolean;
  total?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isDefault}
      className={cn(
        'group flex h-11 min-w-0 flex-1 items-center justify-between gap-3 rounded-full border-b-[3px] border-oxblood bg-ink px-4 text-left text-background',
        'shadow-[0_6px_14px_-8px_rgba(41,70,91,0.6)]',
        'transition-[transform,box-shadow] duration-150 ease-out motion-reduce:transition-none [-webkit-tap-highlight-color:transparent]',
        'hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99]',
        FOCUS,
      )}
    >
      <span className="flex items-center gap-2">
        {isDefault && <Check aria-hidden="true" className="h-4 w-4 shrink-0" strokeWidth={2.5} />}
        <span className="font-engraved text-[13px] font-bold uppercase leading-none tracking-[0.18em]">
          All records
        </span>
      </span>

      <span className="flex items-center gap-2">
        {loading ? (
          <MorseDots className="text-background/80" />
        ) : typeof total === 'number' ? (
          <span className="font-mono text-sm font-semibold tabular-nums">
            {total.toLocaleString()}
            <span className="sr-only"> records</span>
          </span>
        ) : null}
        <ArrowRight
          aria-hidden="true"
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none',
            isDefault && 'opacity-40',
          )}
        />
      </span>
    </button>
  );
}

interface SectionButtonProps {
  collection: Collection;
  count?: number;
  loading: boolean;
  selected: boolean;
  onSelect: (id: string) => void;
}

/**
 * A main section as a compact button: the title (which wraps rather than being
 * cut off) and, quietly beside it, how many records it holds. No arrow and no
 * separate "i": tapping it shows its records, and the explanation appears above
 * them. The chosen one gets a red outline and a faint red wash.
 */
function SectionButton({ collection: c, count, loading, selected, onSelect }: SectionButtonProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(c.id)}
      aria-pressed={selected}
      className={cn(
        'flex min-h-14 w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left',
        'transition-[transform,background-color,border-color] duration-150 ease-out motion-reduce:transition-none [-webkit-tap-highlight-color:transparent]',
        'active:scale-[0.98]',
        FOCUS,
        selected
          ? 'border-oxblood bg-oxblood/[0.07] shadow-[0_0_0_1px_var(--oxblood)]'
          : 'border-foreground/25 bg-ivory shadow-[0_1px_2px_rgba(40,25,10,0.1)] hover:border-ink/60 hover:bg-ink/[0.05]',
      )}
    >
      {(c.id === 'wtc-collapse' || c.id === 'planes') && (
        <span className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-foreground/[0.06]',
          selected ? 'text-oxblood' : 'text-ink',
        )}>
          <CollectionIcon collection={c} className="h-6 w-6" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="title-inked block font-display text-[1.05rem] leading-[1.1] text-foreground sm:text-[1.15rem]">
          <CollectionTitle collection={c} />
        </span>
        {(c.id === 'wtc-collapse' || c.id === 'planes') && (
          <span className="mt-1 block font-mono text-[10px] leading-tight text-foreground/75">
            {c.caption}
          </span>
        )}
      </span>

      <span className="flex shrink-0 items-center gap-1.5 text-foreground/75">
        {loading ? (
          <MorseDots className="opacity-70" />
        ) : typeof count === 'number' ? (
          <span className="font-mono text-xs font-semibold tabular-nums">
            {count.toLocaleString()}
            <span className="sr-only"> records</span>
          </span>
        ) : null}
        {selected && <Check aria-hidden="true" className="h-4 w-4 text-oxblood" strokeWidth={3} />}
      </span>
    </button>
  );
}

/**
 * A special collection (Pager Intercepts, Bitcoin Inscriptions): a larger card
 * that opens the collection's own page on this site. The caption sits beneath
 * the title. Bitcoin shows its fixed inscription count (kept by hand in
 * siteConfig.ts, since it counts what is on the chain); Pager shows the records
 * filed under it, with the loading dots until the first results arrive.
 */
function SpecialLink({
  collection: c,
  count,
  loading,
}: {
  collection: Collection;
  count?: number;
  loading: boolean;
}) {
  const isFixed = typeof c.staticCount === 'number';
  const shown = isFixed ? c.staticCount : count;
  const unit = isFixed ? 'inscriptions' : 'records';

  return (
    <Link
      to={`/${c.id}`}
      className={cn(
        'group flex min-h-16 w-full min-w-0 items-center gap-3 rounded-xl border border-t-[3px] border-foreground/30 border-t-ink bg-ivory px-4 py-3 text-left',
        'shadow-[0_4px_10px_-6px_rgba(40,25,10,0.45)]',
        'transition-[transform,border-color,background-color] duration-150 ease-out motion-reduce:transition-none',
        'hover:-translate-y-0.5 hover:border-ink/60 hover:border-t-ink hover:bg-ink/[0.05] active:translate-y-0 active:scale-[0.99] [-webkit-tap-highlight-color:transparent]',
        FOCUS,
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="title-inked block truncate font-display text-lg leading-tight text-foreground">
          <CollectionTitle collection={c} />
        </span>
        <span className="mt-0.5 block truncate font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/70">
          {c.caption}
        </span>
      </span>
      {loading && !isFixed ? (
        <MorseDots className="opacity-70" />
      ) : typeof shown === 'number' ? (
        <span className="shrink-0 font-mono text-sm font-semibold tabular-nums text-foreground/75">
          {shown.toLocaleString()}
          <span className="sr-only"> {unit}</span>
        </span>
      ) : null}
      <ArrowRight
        aria-hidden="true"
        className="h-5 w-5 shrink-0 text-ink transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none"
      />
    </Link>
  );
}
